import React from 'react';
import useGetListEntriesByFilterParam from 'Hooks/query-hooks/listEntry/useGetListEntriesByFilterParam';
import { PCI_LIST_TYPES, ListTypePlaceholderText, LIST_TYPES } from 'Common/enums';
import LoadMoreButton from 'Components/LoadMoreButton';
import { useTranslation } from 'react-i18next';
import { RQ_LIST_ENTRIES } from 'Utils';
import { useQueryClient } from '@tanstack/react-query';
import { EventNames, EventTypes, trackEvent } from '@brighterion/insights';
import { ListEntryApiGetResponse, ListEntryApiResponseObj } from '@brighterion/types/listEntry';
import { useLocation } from 'react-router-dom';
import Search from './Search';
import Table from './Table';
import styles from './ListEntryTable.css';
import PCISearch from './PCISearch';

type Props = {
    listId: number;
    listType: SingleListType;
    listName: string;
    merchantSpecific: boolean;
    customListType?: string;
    users?: UsersHashmap;
};

// shared for viewing removed list entries and vewing normal list entries
const ListEntryTable = ({ listType, listId, listName, merchantSpecific, customListType, users }: Props) => {
    const queryClient = useQueryClient();
    const { t } = useTranslation();
    const location = useLocation();
    const isViewListEntryHistoryPage = location.pathname.includes('removed-list-entries');
    const [queryFilterValue, setQueryFilterValue] = React.useState('');
    const {
        data,
        isFetching,
        hasNextPage,
        fetchNextPage,
        isFetchingNextPage,
        refetch
    } = useGetListEntriesByFilterParam({
        listId,
        listType,
        filterParam: queryFilterValue,
        statusParam: isViewListEntryHistoryPage ? 'inactive' : 'active',
        // if filter value is empty, don't run it, BE expects a value
        enabled: !!queryFilterValue,
        users
    });

    let totalData = data?.result || [];

    if (listType === LIST_TYPES.RANGE && totalData.length) {
        const rangeData: ListEntryApiGetResponse = [];

        totalData.forEach((item: ListEntryApiResponseObj) => {
            item.range.forEach((rangeItem: { from: number; to: number; comment: string }) => {
                rangeData.push({
                    ...item,
                    comment: rangeItem.comment,
                    range: [{ from: rangeItem.from, to: rangeItem.to, comment: rangeItem.comment }],
                    value: `${rangeItem.from}-${rangeItem.to}`
                });
            });
        });

        totalData = rangeData;
    }

    const isPCIList = PCI_LIST_TYPES.some((v) => v === listType);
    const placeholderText = t(ListTypePlaceholderText[listType]);
    const isLoading = isFetchingNextPage || isFetching;

    const onSubmit = React.useCallback(
        (thisValue: string) => {
            queryClient.removeQueries([RQ_LIST_ENTRIES, listId, thisValue]);
            if (queryFilterValue === thisValue) {
                refetch();
            } else {
                setQueryFilterValue(thisValue);
            }
            trackEvent({
                eventType: EventTypes.track,
                eventName: EventNames.click,
                payload: {
                    clickIdentifier: 'Lists - Search List',
                    text: 'Search List'
                }
            });
        },
        [listId, queryClient, refetch, queryFilterValue]
    );

    return (
        <div className={styles.listTable}>
            {isPCIList ? (
                <>
                    <PCISearch onSubmit={onSubmit} placeholderText={placeholderText} isLoading={isLoading} />
                    <div data-testid='pci-search-exact-text' className={styles.exactMatch}>
                        {t('Exact match required')}
                    </div>
                </>
            ) : (
                <Search onSubmit={onSubmit} placeholderText={placeholderText} isLoading={isLoading} />
            )}
            <Table
                data={totalData}
                listType={listType}
                listName={listName}
                listId={listId}
                merchantSpecific={merchantSpecific}
                isLoading={isLoading}
                customListType={customListType}
            />
            {queryFilterValue && !isFetching && totalData.length === 0 && (
                <div className={styles.noEntries}>{t('No entries found')}</div>
            )}
            {hasNextPage && (
                <div className={styles.loadMoreContainer}>
                    <LoadMoreButton
                        disabled={isLoading}
                        onClick={fetchNextPage}
                        currentCount={totalData.length}
                        totalCount={data?.totalCount}
                    />
                </div>
            )}
        </div>
    );
};

export default ListEntryTable;
